import { create, type StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { createJointActions } from "./sceneActions/jointActions";
import { createBodyActions } from "./sceneActions/bodyActions";
import { createSiteActions } from "./sceneActions/siteActions";
import { createMeshActions } from "./sceneActions/meshActions";
import { createInertiaActions } from "./sceneActions/inertiaActions";
import { createMaterialActions } from "./sceneActions/materialActions";
import { createCoreActions } from "./sceneActions/coreActions";
import { createGeomActions } from "./sceneActions/geomActions";
import { createActuatorActions } from "./sceneActions/actuatorActions";
import { createTendonActions } from "./sceneActions/tendonActions";
import { createEqualityActions, equalityUtils } from "./sceneActions/equalityActions";
import { createFrameActions } from "./sceneActions/frameActions";
import { createCameraActions } from "./sceneActions/cameraActions";
import { createLightActions } from "./sceneActions/lightActions";
import { createSensorActions } from "./sceneActions/sensorActions";
import { createEmptyScene } from "./sceneDefaults";
import type { SceneStoreState } from "./sceneStore.types";
export type {
  SceneStoreState,
  SceneStoreApi,
  SceneStoreGetState,
  SceneStoreSetState,
} from "./sceneStore.types";
export type SceneActionsType = SceneStoreState["actions"];

const EMPTY_RECORD = Object.freeze({}) as Record<string, never>;
const EMPTY_ARRAY = Object.freeze([]) as never[];

const sceneStoreCreator: StateCreator<SceneStoreState> = (set, get) => ({
  ...createEmptyScene(),
  actions: {
    ...createJointActions(set, get),
    ...createBodyActions(set, get),
    ...createSiteActions(set, get),
    ...createMeshActions(set, get),
    ...createInertiaActions(set, get),
    ...createMaterialActions(set, get),
    ...createCoreActions(set, get),
    ...createGeomActions(set, get),
    ...createActuatorActions(set, get),
    ...createTendonActions(set, get),
    ...createEqualityActions(set, get),
    ...createFrameActions(set, get),
    ...createCameraActions(set, get),
    ...createLightActions(set, get),
    ...createSensorActions(set, get),
  },
});

export const useSceneStore = create<SceneStoreState>()(
  devtools(sceneStoreCreator, { name: "SceneStore" })
);

// Selector hooks for efficient subscriptions
// These hooks subscribe to state changes and will cause re-renders when the state changes.

type SliceSelector<TSlice, TResult = TSlice> = (slice: TSlice) => TResult;
type EqualityFn<TResult> = (a: TResult, b: TResult) => boolean;

export const useBodies = <TResult = SceneStoreState["bodies"]>(
  selector?: SliceSelector<SceneStoreState["bodies"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.bodies ?? EMPTY_RECORD) as SceneStoreState["bodies"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useWorldBodies = <TResult = SceneStoreState["worldBodies"]>(
  selector?: SliceSelector<SceneStoreState["worldBodies"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.worldBodies ?? EMPTY_ARRAY) as SceneStoreState["worldBodies"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useJoints = <TResult = SceneStoreState["joints"]>(
  selector?: SliceSelector<SceneStoreState["joints"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.joints ?? EMPTY_RECORD) as SceneStoreState["joints"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useMeshes = <TResult = SceneStoreState["meshes"]>(
  selector?: SliceSelector<SceneStoreState["meshes"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.meshes ?? EMPTY_RECORD) as SceneStoreState["meshes"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useHFields = <TResult = SceneStoreState["hfields"]>(
  selector?: SliceSelector<SceneStoreState["hfields"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.hfields ?? EMPTY_RECORD) as SceneStoreState["hfields"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useMaterials = <TResult = SceneStoreState["materials"]>(
  selector?: SliceSelector<SceneStoreState["materials"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.materials ?? EMPTY_RECORD) as SceneStoreState["materials"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useTextures = <TResult = SceneStoreState["textures"]>(
  selector?: SliceSelector<SceneStoreState["textures"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.textures ?? EMPTY_RECORD) as SceneStoreState["textures"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useSites = <TResult = SceneStoreState["sites"]>(
  selector?: SliceSelector<SceneStoreState["sites"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.sites ?? EMPTY_RECORD) as SceneStoreState["sites"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useCameras = <TResult = SceneStoreState["cameras"]>(
  selector?: SliceSelector<SceneStoreState["cameras"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.cameras ?? EMPTY_RECORD) as SceneStoreState["cameras"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useLights = <TResult = SceneStoreState["lights"]>(
  selector?: SliceSelector<SceneStoreState["lights"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.lights ?? EMPTY_RECORD) as SceneStoreState["lights"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useSceneOption = <TResult = SceneStoreState["option"]>(
  selector?: SliceSelector<SceneStoreState["option"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = state.option;
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useActuators = <TResult = SceneStoreState["actuators"]>(
  selector?: SliceSelector<SceneStoreState["actuators"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.actuators ?? EMPTY_RECORD) as SceneStoreState["actuators"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useTendons = <TResult = SceneStoreState["tendons"]>(
  selector?: SliceSelector<SceneStoreState["tendons"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.tendons ?? EMPTY_RECORD) as SceneStoreState["tendons"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useSensors = <TResult = SceneStoreState["sensors"]>(
  selector?: SliceSelector<SceneStoreState["sensors"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.sensors ?? EMPTY_RECORD) as SceneStoreState["sensors"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useContactPairs = <TResult = SceneStoreState["contactPairs"]>(
  selector?: SliceSelector<SceneStoreState["contactPairs"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.contactPairs ?? EMPTY_ARRAY) as SceneStoreState["contactPairs"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

export const useEqualities = <TResult = SceneStoreState["equality"]>(
  selector?: SliceSelector<SceneStoreState["equality"], TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const slice = (state.equality ?? EMPTY_RECORD) as SceneStoreState["equality"];
      return selector ? selector(slice) : (slice as TResult);
    },
    equalityFn
  );

type BodyUuid = keyof SceneStoreState["bodies"];
type JointUuid = keyof SceneStoreState["joints"];
type MeshUuid = keyof SceneStoreState["meshes"];
type MaterialUuid = keyof SceneStoreState["materials"];
type TextureUuid = keyof SceneStoreState["textures"];
type SiteUuid = keyof SceneStoreState["sites"];
type CameraUuid = keyof SceneStoreState["cameras"];
type LightUuid = keyof SceneStoreState["lights"];
type ActuatorUuid = keyof SceneStoreState["actuators"];
type TendonUuid = keyof SceneStoreState["tendons"];
type EqualityUuid = keyof SceneStoreState["equality"];

type BodyValue = SceneStoreState["bodies"][BodyUuid];
type JointValue = SceneStoreState["joints"][JointUuid];
type MeshValue = SceneStoreState["meshes"][MeshUuid];
type MaterialValue = SceneStoreState["materials"][MaterialUuid];
type TextureValue = SceneStoreState["textures"][TextureUuid];
type SiteValue = SceneStoreState["sites"][SiteUuid];
type CameraValue = SceneStoreState["cameras"][CameraUuid];
type LightValue = SceneStoreState["lights"][LightUuid];
type ActuatorValue = SceneStoreState["actuators"][ActuatorUuid];
type TendonValue = SceneStoreState["tendons"][TendonUuid];
type EqualityValue = SceneStoreState["equality"][EqualityUuid];

export const useBody = <TResult = BodyValue | undefined>(
  uuid: BodyUuid,
  selector?: SliceSelector<BodyValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useBodies((bodies) => {
    const body = bodies[uuid];
    return selector ? selector(body) : (body as TResult);
  }, equalityFn);

export const useJoint = <TResult = JointValue | undefined>(
  uuid: JointUuid,
  selector?: SliceSelector<JointValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useJoints((joints) => {
    const joint = joints[uuid];
    return selector ? selector(joint) : (joint as TResult);
  }, equalityFn);

export const useMesh = <TResult = MeshValue | undefined>(
  uuid: MeshUuid,
  selector?: SliceSelector<MeshValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useMeshes((meshes) => {
    const mesh = meshes[uuid];
    return selector ? selector(mesh) : (mesh as TResult);
  }, equalityFn);

export const useMaterial = <TResult = MaterialValue | undefined>(
  uuid: MaterialUuid,
  selector?: SliceSelector<MaterialValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useMaterials((materials) => {
    const material = materials[uuid];
    return selector ? selector(material) : (material as TResult);
  }, equalityFn);

export const useTexture = <TResult = TextureValue | undefined>(
  uuid: TextureUuid,
  selector?: SliceSelector<TextureValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useTextures((textures) => {
    const texture = textures[uuid];
    return selector ? selector(texture) : (texture as TResult);
  }, equalityFn);

export const useSite = <TResult = SiteValue | undefined>(
  uuid: SiteUuid,
  selector?: SliceSelector<SiteValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useSites((sites) => {
    const site = sites[uuid];
    return selector ? selector(site) : (site as TResult);
  }, equalityFn);

export const useCamera = <TResult = CameraValue | undefined>(
  uuid: CameraUuid,
  selector?: SliceSelector<CameraValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useCameras((cameras) => {
    const camera = cameras[uuid];
    return selector ? selector(camera) : (camera as TResult);
  }, equalityFn);

export const useLight = <TResult = LightValue | undefined>(
  uuid: LightUuid,
  selector?: SliceSelector<LightValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useLights((lights) => {
    const light = lights[uuid];
    return selector ? selector(light) : (light as TResult);
  }, equalityFn);

export const useActuator = <TResult = ActuatorValue | undefined>(
  uuid: ActuatorUuid,
  selector?: SliceSelector<ActuatorValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useActuators((actuators) => {
    const actuator = actuators[uuid];
    return selector ? selector(actuator) : (actuator as TResult);
  }, equalityFn);

export const useTendon = <TResult = TendonValue | undefined>(
  uuid: TendonUuid,
  selector?: SliceSelector<TendonValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useTendons((tendons) => {
    const tendon = tendons[uuid];
    return selector ? selector(tendon) : (tendon as TResult);
  }, equalityFn);

export const useEquality = <TResult = EqualityValue | undefined>(
  uuid: EqualityUuid,
  selector?: SliceSelector<EqualityValue | undefined, TResult>,
  equalityFn?: EqualityFn<TResult>
) =>
  useEqualities((equalities) => {
    const equality = equalities[uuid];
    return selector ? selector(equality) : (equality as TResult);
  }, equalityFn);

// Actions hook (doesn't cause re-renders)
export const useSceneActions = () => useSceneStore((state) => state.actions);

// Non-hook accessors for non-component files
export const getSceneActions = () => useSceneStore.getState().actions;
export const getSceneState = () => {
  const { actions, ...state } = useSceneStore.getState();
  return state;
};

// Type exports
export type BodiesType = SceneStoreState["bodies"];

// Export equality utilities
export { equalityUtils };
